// ประวัติการเข้าระบบรายครั้ง — รวมครั้งที่เข้าไม่สำเร็จ
//
// ต่างจากแท็บ "บุคลากร" ที่แสดงแค่เวลาเข้าระบบล่าสุด (ทับค่าเดิมทุกครั้ง)
// แท็บนี้ตอบได้ว่าใครเข้าจากเครื่องอะไร ที่อยู่ไหน และเข้าไม่ได้เพราะอะไร
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Form, Table } from "react-bootstrap";
import axiosInstance from "../../services/axiosInterceptor";
import {
  DEVICE_LABEL, HOME_COUNTRY, RANGE_OPTIONS, REASON_LABEL, SOURCE_LABEL, countryName,
} from "./shared";

const API = process.env.REACT_APP_API;

const RESULT_OPTIONS = [
  { value: 'all',     label: 'ผลลัพธ์: ทั้งหมด' },
  { value: 'success', label: 'เฉพาะที่สำเร็จ' },
  { value: 'fail',    label: 'เฉพาะที่ไม่สำเร็จ' },
];

const SOURCE_OPTIONS = [
  { value: 'all',    label: 'ประเภท: ทั้งหมด' },
  { value: 'staff',  label: 'เฉพาะบุคลากร' },
  { value: 'portal', label: 'เฉพาะพอร์ทัลนักเรียน' },
];

const NETWORK_OPTIONS = [
  { value: 'all',     label: 'เครือข่าย: ทั้งหมด' },
  { value: 'in',      label: 'เฉพาะในโรงเรียน' },
  { value: 'out',     label: 'เฉพาะนอกโรงเรียน' },
  { value: 'foreign', label: 'เฉพาะจากนอกประเทศ' },
];

const PAGE_SIZE = 200;

export default function LoginHistoryTab() {
  const [rows, setRows]       = useState([]);
  const [days, setDays]       = useState(30);
  const [result, setResult]   = useState('all');
  const [source, setSource]   = useState('all');
  const [network, setNetwork] = useState('all');
  const [search, setSearch]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded]   = useState(0);

  // กรองที่ server ไม่ใช่ที่ client เพราะรายการถูกตัดทีละหน้าก่อนส่งมา
  // ถ้ากรองฝั่ง client จะได้ผลจากในหน้าที่โหลดมาแล้วเท่านั้น ซึ่งไม่ใช่คำตอบที่ถูก
  const fetchPage = useCallback((offset, append) => {
    setBusy(true);
    return axiosInstance
      .get(`${API}/admin/usage/logins`, {
        params: {
          days,
          result,
          source:  source  === 'all' ? undefined : source,
          network: network === 'all' ? undefined : network,
          search:  search.trim() || undefined,
          limit:   PAGE_SIZE,
          offset,
        },
      })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setRows((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PAGE_SIZE);
        setLoaded(offset + data.length);
      })
      .catch(() => {
        if (!append) { setRows([]); setHasMore(false); setLoaded(0); }
      })
      .finally(() => setBusy(false));
  }, [days, result, source, network, search]);

  // เปลี่ยนตัวกรอง = เริ่มนับหนึ่งใหม่ ไม่ใช่ต่อท้ายของเดิม
  // ค้นหาแบบหน่วงเวลา เพื่อไม่ให้ยิง request ทุกตัวอักษรที่พิมพ์
  useEffect(() => {
    const timer = setTimeout(() => fetchPage(0, false), 300);
    return () => clearTimeout(timer);
  }, [fetchPage]);

  const reload = () => fetchPage(0, false);

  const who = (r) => {
    if (r.t_name)  return `${r.t_name} (${r.t_idnumber})`;
    if (r.st_name) return `${r.st_name} (${r.ll_username})`;
    return r.ll_username;
  };

  return (
    <Card className="card-one">
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <Form.Control
          type="search"
          placeholder="ค้นหาชื่อ / รหัสผู้ใช้ / IP / ผู้ให้บริการ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <Form.Select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 160 }}>
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Form.Select value={result} onChange={(e) => setResult(e.target.value)} style={{ maxWidth: 190 }}>
          {RESULT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Form.Select value={source} onChange={(e) => setSource(e.target.value)} style={{ maxWidth: 210 }}>
          {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Form.Select value={network} onChange={(e) => setNetwork(e.target.value)} style={{ maxWidth: 200 }}>
          {NETWORK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
        <Button size="sm" variant="outline-primary" className="ms-auto" onClick={reload} disabled={busy}>
          <i className="ri-refresh-line me-1"></i>รีเฟรช
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-secondary">
              <tr>
                <th style={{ width: 140 }}>วัน-เวลา</th>
                <th>ผู้ใช้</th>
                <th style={{ width: 110 }}>ประเภท</th>
                <th style={{ width: 150 }}>ผล</th>
                <th style={{ width: 140 }}>ที่อยู่ (IP)</th>
                <th style={{ width: 160 }}>ที่ตั้ง / ผู้ให้บริการ</th>
                <th style={{ width: 110 }}>เครือข่าย</th>
                <th style={{ width: 200 }}>อุปกรณ์</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
              ) : rows.map((r) => (
                <tr key={r.ll_id}>
                  <td className="align-middle text-nowrap fs-sm">{r.ll_time_text}</td>
                  <td className="align-middle">{who(r)}</td>
                  <td className="align-middle fs-sm">{SOURCE_LABEL[r.ll_source] || r.ll_source}</td>
                  <td className="align-middle">
                    {r.ll_success
                      ? <Badge bg="success">สำเร็จ</Badge>
                      : <Badge bg="danger">{REASON_LABEL[r.ll_fail_reason] || 'ไม่สำเร็จ'}</Badge>}
                  </td>
                  <td className="align-middle fs-sm text-nowrap">{r.ll_ip || '—'}</td>
                  <td className="align-middle fs-sm">
                    {r.ll_country ? (
                      <React.Fragment>
                        {/* ต่างประเทศต้องสะดุดตาในตารางที่มีแต่ไทย — เป็นแถวที่ต้องตามต่อ */}
                        {r.ll_country === HOME_COUNTRY
                          ? countryName(r.ll_country)
                          : <Badge bg="danger">{countryName(r.ll_country)}</Badge>}
                        {r.ll_isp && <div className="text-secondary fs-xs">{r.ll_isp}</div>}
                      </React.Fragment>
                    ) : <span className="text-secondary">—</span>}
                  </td>
                  <td className="align-middle fs-sm">
                    {r.ll_in_school
                      ? <Badge bg="info">ในโรงเรียน</Badge>
                      : <span className="text-secondary">นอกโรงเรียน</span>}
                  </td>
                  <td className="align-middle fs-sm" title={r.ll_user_agent || ''}>
                    {[r.ll_os, r.ll_browser, DEVICE_LABEL[r.ll_device]].filter(Boolean).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
      <Card.Footer className="d-flex align-items-center gap-2">
        <span className="text-secondary fs-sm">แสดง {rows.length.toLocaleString()} รายการ</span>
        {hasMore && (
          <Button
            size="sm"
            variant="outline-secondary"
            className="ms-auto"
            onClick={() => fetchPage(loaded, true)}
            disabled={busy}
          >
            โหลดเพิ่มอีก {PAGE_SIZE}
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
}
